import React, { useState } from 'react';

// ----- Types -----

type NotificationType =
  | 'connection_request'
  | 'connection_accepted'
  | 'reaction'
  | 'reply'
  | 'direct_message'
  | 'security_alert';

interface NotificationSetting {
  type: NotificationType;
  label: string;
  description: string;
  inApp: boolean;
  email: boolean;
  emailDisabled?: boolean; // security_alert email is always on
}

// ----- Default settings -----

const DEFAULT_SETTINGS: NotificationSetting[] = [
  {
    type: 'connection_request',
    label: 'Connection requests',
    description: 'When someone sends you a connection request',
    inApp: true,
    email: true,
  },
  {
    type: 'connection_accepted',
    label: 'Connection accepted',
    description: 'When someone accepts your connection request',
    inApp: true,
    email: true,
  },
  {
    type: 'reaction',
    label: 'Reactions',
    description: 'When someone reacts to your post',
    inApp: true,
    email: false,
  },
  {
    type: 'reply',
    label: 'Replies',
    description: 'When someone replies to your post',
    inApp: true,
    email: true,
  },
  {
    type: 'direct_message',
    label: 'Direct messages',
    description: 'When you receive a new direct message',
    inApp: true,
    email: true,
  },
  {
    type: 'security_alert',
    label: 'Security alerts',
    description: 'Account security notifications (login from new device, password changes)',
    inApp: true,
    email: true,
    emailDisabled: true,
  },
];

// ----- Main Component -----

/**
 * NotificationSettingsPage — per-type toggle list for in-app and email channels.
 * Security alert email toggle is always on and disabled.
 * Can be used standalone or embedded in AccountSettingsPage.
 */
export function NotificationSettingsPage(): React.JSX.Element {
  const [settings, setSettings] = useState<NotificationSetting[]>(DEFAULT_SETTINGS);

  function handleToggle(
    type: NotificationType,
    channel: 'inApp' | 'email',
    value: boolean,
  ) {
    setSettings((prev) =>
      prev.map((s) =>
        s.type === type ? { ...s, [channel]: value } : s,
      ),
    );
    // TODO: call PATCH /notification-settings
  }

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">Notification Settings</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Choose how you want to be notified.
          </p>
        </div>

        <NotificationSettingsPanel settings={settings} onToggle={handleToggle} />
      </div>
    </div>
  );
}

/**
 * Reusable notification settings panel — used both as a standalone page
 * and embedded in AccountSettingsPage.
 */
export function NotificationSettingsPanel({
  settings,
  onToggle,
}: {
  settings: NotificationSetting[];
  onToggle: (type: NotificationType, channel: 'inApp' | 'email', value: boolean) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface-raised">
      {/* Header row */}
      <div className="flex items-center border-b border-border px-4 py-3">
        <span className="flex-1 text-xs font-semibold uppercase text-text-muted">Type</span>
        <span className="w-16 text-center text-xs font-semibold uppercase text-text-muted">
          In-app
        </span>
        <span className="w-16 text-center text-xs font-semibold uppercase text-text-muted">
          Email
        </span>
      </div>

      {/* Setting rows */}
      {settings.map((setting) => (
        <div
          key={setting.type}
          className="flex items-center border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="flex-1">
            <p className="text-sm font-medium text-text-primary">{setting.label}</p>
            <p className="text-xs text-text-muted">{setting.description}</p>
          </div>

          {/* In-app toggle */}
          <div className="flex w-16 justify-center">
            <Toggle
              checked={setting.inApp}
              onChange={(val) => onToggle(setting.type, 'inApp', val)}
              label={`${setting.label} in-app`}
            />
          </div>

          {/* Email toggle */}
          <div className="relative flex w-16 justify-center">
            <Toggle
              checked={setting.email}
              onChange={(val) => onToggle(setting.type, 'email', val)}
              disabled={setting.emailDisabled}
              label={`${setting.label} email`}
            />
            {setting.emailDisabled && (
              <span
                className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-text-primary px-2 py-0.5 text-[10px] text-white opacity-0 transition hover:opacity-100 peer-hover:opacity-100"
                role="tooltip"
              >
                Always enabled for security
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ----- Toggle Component -----

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      title={disabled ? 'Always enabled for security' : undefined}
      className={`peer relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition ${
        disabled
          ? 'cursor-not-allowed opacity-60'
          : ''
      } ${checked ? 'bg-gail' : 'bg-border'}`}
    >
      <span
        className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
